import { forwardRef, useRef } from 'react'
import { motion, useScroll, useTransform } from 'framer-motion'
import { useMedia } from 'hooks/useMedia'
import { Container, TextContainer } from 'components/container'
import { AnimatedFigcaption } from 'components/animated-figcaption'
import { Details } from 'components/details'
import { Title } from 'components/misc'
import { VideoLoop } from 'components/wide-graphics/video-loop'
import Image from './assets/Image2.png'
import VideoSrc from './assets/shaman Loop-ProRes 422LT-apple.mp4'
import {
  GraphicsDescription,
  SmallGraphics,
  GraphicsImage,
  Graphics,
  Section,
} from './styled'

export const Shamanism = forwardRef<HTMLDivElement, {}>((_, ref) => {
  const isSmall = useMedia([`(min-width: 1024px)`], [true], false)
  const graphicsRef = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll({
    target: graphicsRef,
    offset: ['start end', `end start`],
  })
  const y1 = useTransform(scrollYProgress, [0, 1], [200, -200])
  const y2 = useTransform(scrollYProgress, [0, 1], [400, -400])

  return (
    <Section ref={ref}>
      <VideoLoop url={VideoSrc} />

      <TextContainer>
        <Graphics ref={graphicsRef}>
          <motion.div data-left style={{ y: y1 }} />
          <motion.div data-right style={{ y: y2 }} />
        </Graphics>
        <Title data-menu-title="шаманизм">Шаманизм</Title>
        <p>
          Шаманизм – древняя религия, которая возникла еще во времена палеолита
          и распространилась по всему миру. Шаманизм в Бурятии является одной из
          главных религий, наряду с христианством и буддизмом. В основе этого
          вероучения лежит вера в злых и добрых духов, одухотворение всего, что
          окружает человека: культ огня, родовых гор, матери-земли. Верховным
          божеством является вечно синее небо – Хухэ Мунхэ Тэнгриотцо.
        </p>
        <p>
          Шаманами, как правило, становятся избранные мужчины, в роду которых
          уже были шаманы. Эта роль наследственная, и у избранного шамана должен
          быть "шаманский корень" – утха. У каждого шамана есть своя
          родословная, бввгэй туухэ. В Бурятии существует девять ступеней
          посвящения в шаманы. Заарин – самое высокое духовное звание, которое
          получает шаман после девятого посвящения. Для ритуалов пишутся
          особенные песнопения. В одном из обрядов посвящения шаманы должны
          давать клятву. В{' '}
          <a
            href="https://cyberleninka.ru/article/n/etimologicheskiy-slovar-tengrianskih-terminov-buryat-mongolov"
            target="_blank"
            rel="noreferrer"
          >
            переводе
          </a>{' '}
          с бурятского она звучит примерно так:
        </p>
      </TextContainer>
      <Details>
        <TextContainer>
          <p>
            "Младенцам быть покровителем и сиротам – отцом; бедных, больных не
            обходить, вознаграждением не прельщаться; чужеродных не чуждаться,
            единокровным не угождать; к больному пешком пойти, а к бедному и на
            волах ехать… Пусть эту мою клятву сам Эрлэн-хан слышит, отец
            высокого неба узнает и мать – широкая земля свидетельствует. Если я
            эту клятву исказил или я ее нарушу, то пусть семья моя скоротечно
            погибнет, дом мой развалится с основания; пусть двери мои закроются
            навеки и дымоход дома заглохнет; пусть огонь в очаге моем потухнет,
            опорные столбы юрты расколются".
          </p>
          <p>
            Шаманы знали родословную каждой семьи, контролировали соблюдение
            традиций, обучали соплеменников новым ремеслам, занимались гаданием,
            пересказывали мифы и легенды. Главная функция шаманов сводилась к
            лечению. В шаманизме считается, что болезни приносят злые духи.
            Изгоняют их шаманы посредством камлания.
          </p>
          <p>
            Камлание – это ритуал, во время которого шаман общается с добрыми и
            злыми духами. Для этого шаман входит в транс с помощью песнопений,
            танца и ритмичной музыки. Шаманы используют бубны с колотушкой,
            "конские" трости, колокольчики, увешанные лентами, металлические
            зеркала, шкурки девяти равных зверей, пихтовую кору.
          </p>
          <p>
            В 30-х годах с приходом к власти Сталина шаманов стали сажать в
            тюрьмы и расстреливать. Был наложен запрет на проведение массовых
            религиозных праздников. Оставшиеся в живых шаманы ушли в подполье. В
            результате политики "расшаманивания" прервалась традиция передачи
            особого шаманского дара, старинные обычаи публично уже не
            соблюдались. Но с объявлением перестройки и гласности в конце 1980-х
            началось возрождение шаманизма. Несмотря на репрессии, религия
            оказалась жива.
          </p>
          <p>
            Однако в последние годы в некоторых регионах процесс принятия в
            шаманы значительно демократизировали. Так возникло новое направление
            – неошаманизм. В рамках этого течения посвящение могут пройти и
            люди, у которых нет родового "шаманского корня", они массово обучают
            шаманским обрядам, а также совершают их в ранее не предназначенных
            для этого местах. Это вызывает беспокойство у сторонников
            традиционного шаманизма. Они всеми силами противодействуют "новым
            шаманам", обвиняя их в использовании древнего обычая в политических
            и корыстных целях.
          </p>
        </TextContainer>
      </Details>
      <Container>
        <SmallGraphics>
          {isSmall ? (
            <AnimatedFigcaption
              FigCaptionComponent={GraphicsDescription}
              description="В 1930-е годы советские власти дацан частично разрушили, а большинство монахов расстреляли. В 1990-е храмовый комплекс начали восстанавливать."
            />
          ) : (
            <AnimatedFigcaption
              FigCaptionComponent={GraphicsDescription}
              description='Анинский дацан "Гандан Шаддублинг" был одним из главных буддийских монастырей Бурятии. Он был центром религии, образования, книгопечатания, иконописи и театрального искусства. В 1930-е годы советские власти дацан частично разрушили, а большинство монахов расстреляли. В 1990-е храмовый комплекс начали восстанавливать.'
            />
          )}

          <GraphicsImage src={Image} alt="" />

          {isSmall ? (
            <AnimatedFigcaption
              FigCaptionComponent={GraphicsDescription}
              description='Анинский дацан "Гандан Шаддублинг" был одним из главных буддийских монастырей Бурятии. Он был центром религии, образования, книгопечатания, иконописи и театрального искусства'
            />
          ) : (
            <></>
          )}
        </SmallGraphics>
      </Container>
    </Section>
  )
})
